import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import axios, { acceptDownloadResponse } from "utils/httpClient";

// helpers/services in your project
import { authHeader } from "_helpers";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import { Collapse, Modal, ModalBody } from "reactstrap";

// core components (match your project structure)
import Grid from "components/Grid/GridContainer";
import Col from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";

import SignatureCanvas from "react-signature-canvas";
import { Rnd } from "react-rnd";

import "react-datetime/css/react-datetime.css";
import "./custom-style.css";

// pdf.js
import * as pdfjsLib from "pdfjs-dist/build/pdf";
// VERY IMPORTANT: ต้องคัดลอก node_modules/pdfjs-dist/build/pdf.worker.min.js → public/pdf.worker.min.js
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

// -------------------- Styles --------------------
const styles = {
    cardCategoryWhite: {
        color: "rgba(255,255,255,.62)",
        margin: 0,
        fontSize: "14px",
    },
    cardTitleWhite: {
        color: "#FFFFFF",
        marginTop: 0,
        minHeight: "auto",
        fontWeight: 300,
        fontFamily: '"Sarabun", sans-serif',
        marginBottom: "3px",
        textDecoration: "none",
    },
    cardTitleCustom: {
        background: "linear-gradient(to right, #2193b0, #6dd5ed)",
        margin: "-20px 15px 15px 15px",
    },
};
const useStyles = makeStyles(styles);

// -------------------- Modal วาดลายเซ็น --------------------
const SignPadModal = ({ open, onClose, onSave }) => {
    const sigRef = useRef(null);

    function handleClear() {
        if (sigRef.current) sigRef.current.clear();
    }

    function handleDone() {
        if (!sigRef.current || sigRef.current.isEmpty()) {
            alert("กรุณาเซ็นก่อน");
            return;
        }
        const dataUrl = sigRef.current
            .getTrimmedCanvas()
            .toDataURL("image/png");
        onSave(dataUrl);
    }

    return (
        <Modal isOpen={open} toggle={onClose}>
            <div className="modal-header justify-content-center">
                <h4>โปรดลงลายมือชื่อ</h4>
            </div>
            <ModalBody>
                <div
                    style={{
                        border: "1px solid #999",
                        borderRadius: 8,
                        background: "#fff",
                    }}
                >
                    <SignatureCanvas
                        ref={sigRef}
                        penColor="blue"
                        canvasProps={{
                            width: 500,
                            height: 200,
                            style: {
                                height: "200px",
                                borderRadius: "8px",
                            },
                        }}
                    />
                </div>

                <div
                    style={{
                        marginTop: 10,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        justifyContent: "center",
                    }}
                >
                    <Button color="warning" onClick={handleClear}>
                        ลบ
                    </Button>
                    <Button color="success" onClick={handleDone}>
                        บันทึกลายเซ็น
                    </Button>
                    <Button color="danger" onClick={onClose}>
                        ยกเลิก
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );
};

// -------------------- กล่องลายเซ็น draggable/resize --------------------
// box = { xPx, yPx, wPx, hPx } เป็นหน่วย "พิกเซลของหน้ากระดาษจริงที่เราวาดลง canvas"
// scaleX/scaleY = สเกลจริงจาก DOM (หลังถูก transform: scale(...))
const SignatureBox = ({ signatureDataUrl, box, onChange, scaleX, scaleY }) => {
    const scaledSize = {
        width: box.wPx * scaleX,
        height: box.hPx * scaleY,
    };

    const scaledPos = {
        x: box.xPx * scaleX,
        y: box.yPx * scaleY,
    };

    return (
        <Rnd
            bounds="parent"
            size={scaledSize}
            position={scaledPos}
            onDragStop={(e, d) => {
                onChange({
                    ...box,
                    xPx: d.x / scaleX,
                    yPx: d.y / scaleY,
                });
            }}
            onResizeStop={(e, direction, ref, delta, position) => {
                onChange({
                    xPx: position.x / scaleX,
                    yPx: position.y / scaleY,
                    wPx: parseFloat(ref.style.width) / scaleX,
                    hPx: parseFloat(ref.style.height) / scaleY,
                });
            }}
            style={{
                border: "1px dashed #444",
                backgroundColor: "rgba(255,255,255,0.15)",
                cursor: "move",
                borderRadius: "4px",
                boxShadow: "0 4px 12px rgba(0,0,0,.2)",
                pointerEvents: "auto",
            }}
        >
            <img
                src={signatureDataUrl}
                alt="signature"
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    pointerEvents: "none",
                    userSelect: "none",
                }}
            />
        </Rnd>
    );
};

// -------------------- Main Component --------------------
const PdfSign = (props) => {
    const classes = useStyles();
    const history = useHistory();
    const user = useSelector((state) => state.auth.user);

    const { location } = props;
    const navState = location?.state || {};

    // formSubmissionId (สำหรับส่งกลับ backend ตอนบันทึกลายเซ็น)
    const templateId = navState.templateId || 1; //รับ Parameter templateId ว่าเป็นรายงานไหน
    const referenceKey = navState.referenceKey || 210016;  //รับ Parameter referenceKey ว่าเป็นรายการของใคร

    // pageSizePt ใช้สำหรับ map พิกัด -> พิกัด pt ใน PDF
    const [pageSizePt, setPageSizePt] = useState(
        navState.pageSizePt || { width: 595.0, height: 842.0 } // fallback A4
    );

    // ไบต์ของ PDF (Uint8Array)
    const [pdfBytes, setPdfBytes] = useState(null);

    const [pdfBase64, setPdfBase64] = useState(null);
    // ขนาด bitmap จริงของหน้า PDF หลัง render ด้วย baseScale
    const [pageBitmapPx, setPageBitmapPx] = useState({
        width: 0,
        height: 0,
    });

    // scale ที่ใช้ "แสดงผล" ให้พอดีกับความกว้าง/ความสูง container (responsive)
    const [fitScale, setFitScale] = useState(1);

    // สเกลจริงจาก DOM (คำนวณจาก getBoundingClientRect เพื่อเลี่ยงการเพี้ยนเวลาไม่มี/มี scrollbar)
    const [domScale, setDomScale] = useState({ x: 1, y: 1 });

    // canvas จริง (เรนเดอร์ความละเอียดเต็ม)
    const canvasRef = useRef(null);

    // container ของ PDF viewer ที่เราจะใช้วัดความกว้าง/สูง
    const containerRef = useRef(null);

    // FRAME (outer) and LAYER (inner)
    const frameRef = useRef(null);
    // เลเยอร์ที่ถูกสเกลด้วยขนาดกล่อง (ไม่ใช้ CSS transform)
    const scaledLayerRef = useRef(null);

    // ลายเซ็น base64 (data URL)
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);

    // กล่องลายเซ็น (ตำแหน่ง/ขนาดในหน่วย "bitmap จริง")
    const [sigBox, setSigBox] = useState({
        xPx: 100,
        yPx: 100,
        wPx: 200,
        hPx: 80,
    });

    // modal states
    const [modal, setModal] = useState(false); // confirm apply signature
    const [modal2, setModal2] = useState(false); // success
    const [modalError, setModalError] = useState(false); // error
    const [modalCancel, setModalCancel] = useState(false); // cancel?
    const [errMsg, setErrMsg] = useState([]);
    const [icon, setIcon] = useState("far fa-question-circle");
    const [isOpen, setIsOpen] = useState(false);
    const [isShowList, setIsShowList] = useState(true);

    // modal เซ็นลายมือชื่อ
    const [showSignPadModal, setShowSignPadModal] = useState(false);
    // จำตำแหน่งที่ดับเบิลคลิก (ในหน่วย bitmap จริง ก่อนย่อ)
    const [pendingClickPos, setPendingClickPos] = useState(null);

    // moment/locale TH (ถ้าคุณใช้ตรงอื่น)
    const moment = require("moment");
    require("moment/locale/th");

    // -------------------- helper: base64 -> Uint8Array --------------------
    function base64PdfToUint8Array(pdfBase64) {
        const commaIndex = pdfBase64.indexOf(",");
        const pureBase64 =
            commaIndex >= 0 ? pdfBase64.substring(commaIndex + 1) : pdfBase64;
        const binaryString = atob(pureBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    }

    // -------------------- โหลด PDF --------------------
    useEffect(() => {
        if (navState.pdfBytes && navState.pageSizePt) {
            setPdfBytes(navState.pdfBytes);
            setPageSizePt(navState.pageSizePt);
            return;
        }

        (async () => {
            try {
                const res = await axios.get("/api/v1/reportview/or", {
                    headers: authHeader(),
                });

                const g = res?.data;
                if (!g) return;

                const bytes = base64PdfToUint8Array(g.pdfBase64);

                setPdfBase64(g.pdfBase64);
                setPdfBytes(bytes);

                if (g.pageSizePt?.width && g.pageSizePt?.height) {
                    setPageSizePt(g.pageSizePt);
                }
            } catch (err) {
                console.error(err);
            }
        })();
    }, [navState.pdfBytes, navState.pageSizePt]);

    // -------------------- เรนเดอร์ PDF ลง canvas (full quality) --------------------
    useEffect(() => {
        (async function renderPdf() {
            try {
                if (!pdfBytes || !canvasRef.current) return;

                const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
                const pdf = await loadingTask.promise;
                const page = await pdf.getPage(1);

                // baseScale: คุณภาพจริงของ canvas
                const baseScale = 1.5;
                const viewport = page.getViewport({ scale: baseScale });

                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                setPageBitmapPx({
                    width: viewport.width,
                    height: viewport.height,
                });

                const renderContext = {
                    canvasContext: ctx,
                    viewport,
                };
                await page.render(renderContext).promise;
            } catch (err) {
                console.error("renderPdf error:", err);
            }
        })();
    }, [pdfBytes]);

    // -------------------- คำนวณ fitScale (ย่อ/ขยายตามความกว้าง container เท่านั้น) --------------------
    const recalcFitScale = useCallback(() => {
        if (!containerRef.current) return;
        if (!pageBitmapPx.width || !pageBitmapPx.height) return;
        const container = containerRef.current;
        const maxW = (container.clientWidth || 0) - 16;
        const scaleW = maxW / pageBitmapPx.width;
        const next = Math.min(scaleW, 1);
        setFitScale((prev) => (Math.abs(prev - next) > 1e-6 ? next : prev));
    }, [pageBitmapPx.width, pageBitmapPx.height]);

    // เรียกตอนรู้ขนาด pdf แล้ว + ตอบสนองการเปลี่ยนขนาดหน้าต่าง
    useEffect(() => {
        const handleResize = () => recalcFitScale();
        if (pageBitmapPx.width && pageBitmapPx.height) {
            recalcFitScale();
            window.addEventListener("resize", handleResize);
            window.addEventListener("orientationchange", handleResize);
        }
        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("orientationchange", handleResize);
        };
    }, [pageBitmapPx, recalcFitScale]);

    // ไม่ใช้ ResizeObserver กับ container เพื่อตัด loop จากการสเกล → ใช้เพียง window resize/orientationchange

    // -------------------- อัปเดต domScale จากขนาดจริงของ FRAME (เลี่ยง jitter/loop) --------------------
    const updateDomScale = useCallback(() => {
        if (!frameRef.current) return;
        const rect = frameRef.current.getBoundingClientRect();
        if (!pageBitmapPx.width || !pageBitmapPx.height) return;
        const nx = rect.width / pageBitmapPx.width;
        const ny = rect.height / pageBitmapPx.height;
        setDomScale((old) => (Math.abs(old.x - nx) > 1e-6 || Math.abs(old.y - ny) > 1e-6 ? { x: nx, y: ny } : old));
    }, [pageBitmapPx.width, pageBitmapPx.height]);

    useEffect(() => { updateDomScale(); }, [fitScale, pageBitmapPx.width, pageBitmapPx.height, updateDomScale]);

    // -------------------- ยืนยันตำแหน่งลายเซ็น -> ส่ง backend --------------------
    const handleConfirmSignature = async () => {
        if (!signatureDataUrl) {
            alert("กรุณาสร้างลายเซ็นก่อน");
            return;
        }

        // ใช้ templateId จาก navState (มาจากตอน generate report)
        if (!templateId) {
            alert("ไม่พบรหัสรายงาน (templateId) จากหน้า generate");
            return;
        }

        // ต้องรู้ขนาด bitmap ก่อน
        if (!pageBitmapPx.width || !pageBitmapPx.height) {
            alert("ยังไม่ทราบขนาดหน้ากระดาษ (pageBitmapPx)");
            return;
        }

        // แปลงตำแหน่ง/ขนาดจากพิกเซล bitmap -> pt ของ PDF
        const pdfWpt = pageSizePt.width;
        const pdfHpt = pageSizePt.height;

        const scaleX = pdfWpt / pageBitmapPx.width;
        const scaleY = pdfHpt / pageBitmapPx.height;

        const xPt = sigBox.xPx * scaleX;
        const wPt = sigBox.wPx * scaleX;

        // PDF y-origin อยู่ล่าง → ต้องกลับแกนจาก bitmap ที่ origin อยู่บน
        const bottomPx = sigBox.yPx + sigBox.hPx;
        const pdfYBottomPx = pageBitmapPx.height - bottomPx;
        const yPt = pdfYBottomPx * scaleY;
        const hPt = sigBox.hPx * scaleY;

        // payload ตาม API ล่าสุด:
        // record ApplySignatureRequest(
        //   string SignatureImageBase64,
        //   int Page,
        //   decimal X, decimal Y, decimal Width, decimal Height,
        //   string? OwnerUserId = null
        // )
        /*
        const payload = {
            signatureImageBase64: signatureDataUrl,  // data:image/png;base64,....
            page: 1,                                 // ตอนนี้ลงหน้า 1
            x: xPt,
            y: yPt,
            width: wPt,
            height: hPt,
            ownerUserId: user?.userName || null,     // ส่งหรือไม่ส่งก็ได้
        };

        try {
            setIcon("fa fa-spinner fa-w-16 fa-spin fa-lg");

            const res = await axios.post(
                `/api/v1/reportview/${reportId}/apply-signature`,
                payload,
                {
                    headers: authHeader(),
                    responseType: "blob", // รับ PDF กลับมาเป็น binary
                }
            );

            // เปิด PDF ที่เซ็นแล้ว
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url);

            setIcon("far fa-question-circle");
            setModal(false);
            setModal2(true);
        } catch (e) {
            console.error(e);
            setIcon("far fa-question-circle");
            setModal(false);
            setErrMsg([
                e?.response?.data?.msg ||
                e?.message ||
                "ไม่สามารถบันทึกตำแหน่งลายเซ็นได้",
            ]);
            setModalError(true);
        }
        */
        const body = {
            templateId,
            referenceKey,              // เช่น HN หรือ FlowID
            //parameters: { HN: hn },        // ถ้ามี parameter ที่ใช้ตอน gen report
            pdfBase64: pdfBase64,
            signatureImageBase64: signatureDataUrl,
            page: 1,//currentPage,
            x: xPt,
            y: yPt,
            width: wPt,
            height: hPt,
            ownerUserId: null,//currentUserName,  // หรือ null ก็ได้
        };

        const res = await axios('/api/v1/reportview/apply-signature', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: body,
          responseType: "blob",
          validateStatus: acceptDownloadResponse
        });

        const blob = res.data;
        // เอา blob ไปเปิดเป็น PDF, หรือดาวน์โหลด, หรือแสดงแทนตัวเดิมก็ได้

    };

    const handleConfirmModalYes = async (evt) => {
        evt.preventDefault();
        await handleConfirmSignature().catch(error => console.error(error));
    };

    return (
        <div className="pdf-sign-wrapper">
            <Grid>
                <Col
                    xs={12}
                    sm={12}
                    md={12}
                    style={{ display: isShowList ? "block" : "none" }}
                >
                    <Card>
                        <CardHeader color="primary">
                            <h4 className={classes.cardTitleWhite}>
                                ลงลายเซ็นบนเอกสาร
                            </h4>
                        </CardHeader>

                        <CardBody>
                            {/* กรอบ responsive: ใช้ ref วัดขนาดเพื่อคำนวณ fitScale */}
                            <div
                                ref={containerRef}
                                style={{
                                    overflowY: "auto",
                                    paddingRight: 8,
                                    maxHeight: "90vh",
                                }}
                            >
                                {/* FRAME ชั้นนอก: ขนาดหลังย่อจริง ทำให้ layout/scroll ถูกต้อง */}
                                <div
                                    ref={frameRef}
                                    style={{
                                        width: pageBitmapPx.width * fitScale,
                                        height: pageBitmapPx.height * fitScale,
                                        background: "#fff",
                                        boxShadow: "0 10px 30px rgba(0,0,0,.15)",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        position: "relative",
                                        margin: "0 auto",
                                    }}
                                >
                                    {/* LAYER ชั้นใน: ขนาดเต็มของ FRAME (ไม่ใช้ transform) */}
                                    <div
                                        ref={scaledLayerRef}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                            cursor: "crosshair",
                                        }}
                                        onDoubleClick={(e) => {
                                            if (!scaledLayerRef.current) return;
                                            const rect = scaledLayerRef.current.getBoundingClientRect();

                                            // คำนวณสเกลจริงจาก DOM (กัน offset ที่เพี้ยนเวลาไม่มี/มี scrollbar)
                                            const sX = rect.width / (pageBitmapPx.width || 1);
                                            const sY = rect.height / (pageBitmapPx.height || 1);

                                            const xReal = (e.clientX - rect.left) / sX;
                                            const yReal = (e.clientY - rect.top) / sY;

                                            setPendingClickPos({ x: xReal, y: yReal });

                                            const defaultW = 200;
                                            const defaultH = 80;
                                            if (!signatureDataUrl) {
                                                setShowSignPadModal(true);
                                            } else {
                                                setSigBox({
                                                    xPx: xReal - defaultW / 2,
                                                    yPx: yReal - defaultH / 2,
                                                    wPx: defaultW,
                                                    hPx: defaultH,
                                                });
                                            }
                                        }}
                                    >
                                        <canvas
                                            ref={canvasRef}
                                            style={{
                                                display: "block",
                                                background: "#fff",
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        />

                                        {signatureDataUrl ? (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                <SignatureBox
                                                    signatureDataUrl={signatureDataUrl}
                                                    box={sigBox}
                                                    onChange={setSigBox}
                                                    scaleX={domScale.x}
                                                    scaleY={domScale.y}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            {/* ปุ่มควบคุม */}
                            <div
                                style={{
                                    marginTop: 20,
                                    display: "flex",
                                    gap: 8,
                                    flexWrap: "wrap",
                                }}
                            >
                                <Button
                                    color="info"
                                    onClick={() => {
                                        setShowSignPadModal(true);
                                    }}
                                >
                                    {signatureDataUrl ? "แก้ไขลายเซ็น" : "สร้างลายเซ็น"}
                                </Button>

                                <Button
                                    color="success"
                                    onClick={() => {
                                        setModal(true);
                                    }}
                                >
                                    ยืนยันตำแหน่งและบันทึก
                                </Button>
                            </div>
                        </CardBody>

                        <CardFooter />
                    </Card>
                </Col>
            </Grid>

            {/* ==== Modal วาดลายเซ็น ==== */}
            <SignPadModal
                open={showSignPadModal}
                onClose={() => {
                    setShowSignPadModal(false);
                }}
                onSave={(dataUrl) => {
                    setSignatureDataUrl(dataUrl);
                    setShowSignPadModal(false);

                    if (pendingClickPos) {
                        const defaultW = 200;
                        const defaultH = 80;
                        setSigBox({
                            xPx: pendingClickPos.x - defaultW / 2,
                            yPx: pendingClickPos.y - defaultH / 2,
                            wPx: defaultW,
                            hPx: defaultH,
                        });
                        setPendingClickPos(null);
                    }
                }}
            />

            {/* ====== Modals ยืนยัน / success / error ====== */}
            <CardFooter>
                <Collapse isOpen={isOpen}></Collapse>

                {/* Confirm apply signature */}
                <Modal toggle={() => setModal(false)} isOpen={modal}>
                    <div className="modal-header justify-content-center">
                        <div>
                            <i
                                className={icon}
                                style={{ color: "blue", fontSize: "2em" }}
                            ></i>
                        </div>
                    </div>
                    <ModalBody>
                        <p style={{ textAlign: "center", fontSize: "1.8em" }}>
                            {icon !== "fa fa-spinner fa-w-16 fa-spin fa-lg"
                                ? "ยืนยันการฝังลายเซ็นลงในเอกสาร?"
                                : "กำลังบันทึกตำแหน่งลายเซ็น..."}
                        </p>
                    </ModalBody>
                    <div className="modal-footer">
                        <Button
                            color="success"
                            type="button"
                            onClick={handleConfirmModalYes}
                            disabled={icon === "fa fa-spinner fa-w-16 fa-spin fa-lg"}
                        >
                            <i
                                className={"fa fa-thumbs-up"}
                                style={{
                                    fontSize: "1.8em",
                                    padding: "0 5px 5px 0",
                                }}
                            ></i>
                            <h4>ยืนยัน</h4>
                        </Button>
                        <Button
                            color="danger"
                            type="button"
                            onClick={() => {
                                setIcon("far fa-question-circle");
                                setModal(false);
                            }}
                        >
                            <i
                                className={"fa fa-thumbs-down"}
                                style={{
                                    fontSize: "1.8em",
                                    padding: "0 5px 5px 0",
                                }}
                            ></i>
                            <h4>ยกเลิก</h4>
                        </Button>
                    </div>
                </Modal>

                {/* Success */}
                <Modal isOpen={modal2}>
                    <div className="modal-header justify-content-center">
                        <div>
                            <i
                                className={"fas fa-check"}
                                style={{ color: "green", fontSize: "4em" }}
                            ></i>
                        </div>
                    </div>
                    <ModalBody>
                        <p
                            style={{
                                textAlign: "center",
                                fontSize: "1.8em",
                            }}
                        >
                            บันทึกการลงนามสำเร็จ
                        </p>
                    </ModalBody>
                    <div className="modal-footer">
                        <Button
                            color="info"
                            type="button"
                            onClick={() => {
                                history.goBack();
                            }}
                        >
                            <i
                                className={"fa fa-thumbs-up"}
                                style={{
                                    fontSize: "1.8em",
                                    padding: "0 5px 5px 0",
                                }}
                            ></i>
                            <h4>ตกลง</h4>
                        </Button>
                    </div>
                </Modal>

                {/* Error */}
                <Modal toggle={() => setModalError(false)} isOpen={modalError}>
                    <div className="modal-header justify-content-center">
                        <div>
                            <i
                                className="fa fa-times"
                                style={{ color: "red", fontSize: "2em" }}
                            ></i>
                        </div>
                    </div>
                    <ModalBody>
                        <p>ไม่สามารถบันทึกการลงนามได้</p>
                        <div>{errMsg}</div>
                    </ModalBody>
                    <div className="modal-footer justify-content-center">
                        <Button
                            color="primary"
                            type="button"
                            onClick={() => setModalError(false)}
                        >
                            <h4>ตกลง</h4>
                        </Button>
                    </div>
                </Modal>

                {/* Optional: modalCancel */}
                <Modal toggle={() => setModalCancel(false)} isOpen={modalCancel}>
                    <div className="modal-header justify-content-center">
                        <div>
                            <i
                                className={icon}
                                style={{ color: "red", fontSize: "2em" }}
                            ></i>
                        </div>
                    </div>
                    <ModalBody>
                        <p
                            style={{
                                textAlign: "center",
                                fontSize: "1.8em",
                            }}
                        >
                            {icon !== "fa fa-spinner fa-w-16 fa-spin fa-lg"
                                ? "ปิดหน้าฟอร์มนี้หรือไม่?"
                                : "กำลังตรวจสอบข้อมูล"}
                        </p>
                    </ModalBody>
                    <div className="modal-footer">
                        <Button
                            color="success"
                            type="button"
                            onClick={() => {
                                setModalCancel(false);
                            }}
                            disabled={icon === "fa fa-spinner fa-w-16 fa-spin fa-lg"}
                        >
                            <i
                                className={"fa fa-thumbs-up"}
                                style={{
                                    fontSize: "1.8em",
                                    padding: "0 5px 5px 0",
                                }}
                            ></i>
                            <h4>ยืนยัน</h4>
                        </Button>
                        <Button
                            color="danger"
                            type="button"
                            onClick={() => {
                                setIcon("far fa-question-circle");
                                setModalCancel(false);
                            }}
                        >
                            <i
                                className={"fa fa-thumbs-down"}
                                style={{
                                    fontSize: "1.8em",
                                    padding: "0 5px 5px 0",
                                }}
                            ></i>
                            <h4>ปฏิเสธ</h4>
                        </Button>
                    </div>
                </Modal>
            </CardFooter>
        </div>
    );
};

export default PdfSign;
